import React, { useState, useEffect, useCallback } from 'react';
import { Download, X, Loader2, CheckCircle, FileSpreadsheet } from 'lucide-react';

// Global export state manager
let exportListeners = [];
let exportJobs = [];
let jobIdCounter = 0;

const notifyListeners = () => {
  exportListeners.forEach(fn => fn([...exportJobs]));
};

export const startExport = (label, generateFn) => {
  const id = ++jobIdCounter;
  const job = {
    id,
    label,
    status: 'preparing', // preparing | ready | error
    progress: 0,
    blob: null,
    fileName: null,
    error: null,
    createdAt: Date.now()
  };
  exportJobs.push(job);
  notifyListeners();

  // Run generation in background
  (async () => {
    try {
      const { blob, fileName } = await generateFn((progress) => {
        job.progress = progress;
        notifyListeners();
      });
      job.status = 'ready';
      job.blob = blob;
      job.fileName = fileName;
      notifyListeners();
    } catch (err) {
      console.error('Export error:', err);
      job.status = 'error';
      job.error = err.message || 'Export hatası';
      notifyListeners();
    }
  })();

  return id;
};

const removeJob = (id) => {
  const idx = exportJobs.findIndex(j => j.id === id);
  if (idx > -1) {
    // Revoke blob URL if exists
    if (exportJobs[idx].blob) {
      URL.revokeObjectURL(exportJobs[idx]._blobUrl);
    }
    exportJobs.splice(idx, 1);
    notifyListeners();
  }
};

const downloadJob = (job) => {
  if (!job.blob) return;
  const url = URL.createObjectURL(job.blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = job.fileName || 'export.xlsx';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);

  // Auto remove after download
  setTimeout(() => removeJob(job.id), 2000);
};

export default function ExportNotification() {
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    const listener = (newJobs) => setJobs(newJobs);
    exportListeners.push(listener);
    return () => {
      const idx = exportListeners.indexOf(listener);
      if (idx > -1) exportListeners.splice(idx, 1);
    };
  }, []);

  if (jobs.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 space-y-2 max-w-sm">
      {jobs.map(job => (
        <div
          key={job.id}
          className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden animate-in slide-in-from-bottom duration-300"
        >
          <div className="p-4">
            <div className="flex items-start gap-3">
              {/* Icon */}
              <div className="flex-shrink-0 mt-0.5">
                {job.status === 'preparing' && (
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                    <Loader2 className="w-5 h-5 text-blue-600 dark:text-blue-400 animate-spin" />
                  </div>
                )}
                {job.status === 'ready' && (
                  <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                )}
                {job.status === 'error' && (
                  <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
                    <FileSpreadsheet className="w-5 h-5 text-red-600 dark:text-red-400" />
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800 dark:text-white truncate">
                  {job.label}
                </p>
                {job.status === 'preparing' && (
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    Dosya hazırlanıyor...
                    {job.progress > 0 && ` %${job.progress}`}
                  </p>
                )}
                {job.status === 'ready' && (
                  <p className="text-xs text-green-600 dark:text-green-400 mt-0.5">
                    Dosya hazır
                  </p>
                )}
                {job.status === 'error' && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">
                    {job.error}
                  </p>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 flex-shrink-0">
                {job.status === 'ready' && (
                  <button
                    onClick={() => downloadJob(job)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded-lg transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" />
                    İndir
                  </button>
                )}
                <button
                  onClick={() => removeJob(job.id)}
                  className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Progress bar */}
          {job.status === 'preparing' && (
            <div className="h-1 bg-slate-100 dark:bg-slate-700">
              <div
                className="h-full bg-blue-500 transition-all duration-300 ease-out"
                style={{ width: job.progress > 0 ? `${job.progress}%` : '30%' }}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
